import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useTranslation } from 'react-i18next';
import { ProductStatisticsItem } from '../../types/statistics';

interface ProductsDonutChartProps {
  productsData: ProductStatisticsItem[];
}

const ProductsDonutChart: React.FC<ProductsDonutChartProps> = ({ productsData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate total quantity (amount) of all products
  const totalQuantity = productsData.reduce((sum, item) => sum + item.quantity, 0);

  const chartData = productsData.map((item, index) => {
    const percentage = totalQuantity > 0 ? (item.quantity / totalQuantity) * 100 : 0;
    const roundedPercentage = Math.round(percentage * 10) / 10; // Round to 1 decimal place
    return {
      id: index,
      value: item.quantity,
      label: `${item.productName}: ${item.quantity} (${roundedPercentage}%)`,
      productName: item.productName,
      quantity: item.quantity,
      percentage: roundedPercentage,
    };
  });

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('statistics.productsStatistics')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <Typography variant="body1" color="text.secondary">
              {t('statistics.noData')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const chartHeight = isMobile ? 300 : 400;
  const innerRadius = isMobile ? 40 : 60;
  const outerRadius = isMobile ? 80 : 120;
  const chartMargin = isMobile 
    ? { top: 20, right: 10, bottom: 20, left: 10 }
    : { top: 20, right: 20, bottom: 20, left: 20 };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('statistics.productsStatistics')}
        </Typography>
        <Box sx={{ width: '100%', height: isMobile ? 'auto' : chartHeight, display: 'flex', justifyContent: 'center' }}>
          <PieChart
            series={[
              {
                data: chartData,
                innerRadius,
                outerRadius,
                paddingAngle: 2,
                cornerRadius: 5,
                arcLabel: () => '', // No labels on the donut, use legend and tooltip instead
                arcLabelMinAngle: 10,
                valueFormatter: () => '',
              },
            ]}
            height={chartHeight}
            margin={chartMargin}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductsDonutChart;

